import { createContext, ReactNode, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import useActiveConfig from "datasets/pages/settings/hooks/useActiveConfig";
import useDraftConfig from "datasets/pages/settings/hooks/useDraftConfig";
import { useQueryParam, NumberParam } from "use-query-params";
import useErrorToast from "~/contexts/error-toast";
import { useFetcher } from "~/hooks/useFetcher";
import CeConfigResponse, { CeConfigDomain } from "~/types/interfaces/ce-config";

export interface ConfiguratorContextType {
  config?: Partial<CeConfigResponse>;
  editing: boolean;
  loading: boolean;
  addDomain: (domain: CeConfigDomain) => void;
  updateDomain: (id: string, domain: CeConfigDomain) => void;
  shiftDomain: (id: string, direction: "up" | "down") => void;
  removeDomain: (id: string) => void;
  setSystemInstructions: (prompt: string) => void;
  setSubjectLabel: (label: string) => void;
  clearDraftConfig: () => void;
  setDraftConfig: (
    value:
      | Partial<CeConfigResponse>
      | ((prev: Partial<CeConfigResponse> | undefined) => Partial<CeConfigResponse> | undefined)
      | undefined
  ) => void;
}

const ConfiguratorContext = createContext<ConfiguratorContextType | undefined>(undefined);

export const ConfiguratorProvider = ({ children }: { children: ReactNode }) => {
  const { showError } = useErrorToast();
  const { datasetId } = useParams();
  const [version] = useQueryParam("version", NumberParam);
  const [draftConfig, setDraftConfig] = useDraftConfig();
  const { data: activeConfig, isLoading: loadingActive } = useActiveConfig();
  const { data: versionConfig, isLoading: loadingVersion } = useFetcher<CeConfigResponse>(
    version ? `/api/cohort-explorer/configurator/configs/${version}?dataset_id=${datasetId}` : null,
    {
      onError: () => showError("Unable to load configuration version"),
    }
  );

  const { config, editing, loading } = useMemo(() => {
    if (draftConfig) {
      return { config: draftConfig, editing: true, loading: false };
    }
    if (version) {
      return { config: versionConfig, editing: false, loading: loadingVersion };
    }
    return { config: activeConfig, editing: false, loading: loadingActive };
  }, [draftConfig, activeConfig, versionConfig, version, loadingActive, loadingVersion]);

  const addDomain = (domain: CeConfigDomain) => {
    const tables = [...(config?.content?.tables ?? []), domain];
    setDraftConfig({
      ...config,
      content: {
        ...config?.content,
        tables,
      },
    });
  };

  const updateDomain = (id: string, domain: CeConfigDomain) => {
    if (config?.content) {
      const updatedTables = config.content.tables.map((table) =>
        table.id === id ? domain : table
      );
      setDraftConfig({
        ...config,
        content: {
          ...config.content,
          tables: updatedTables,
        },
      });
    }
  };

  const shiftDomain = (id: string, direction: "up" | "down") => {
    const tables = config?.content?.tables;

    if (tables) {
      const currentIndex = tables.findIndex((table) => table.id === id);
      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      // Swap the items in the array
      [tables[targetIndex], tables[currentIndex]] = [tables[currentIndex], tables[targetIndex]];
      setDraftConfig({
        ...config,
        content: {
          ...config?.content,
          tables,
        },
      });
    }
  };

  const removeDomain = (id: string) => {
    if (config?.content) {
      const filteredTables = config.content.tables.filter((table) => table.id !== id);
      setDraftConfig({ ...config, content: { ...config.content, tables: filteredTables } });
    }
  };

  const setSubjectLabel = (label: string) => {
    if (config) {
      setDraftConfig({
        ...config,
        content: {
          ...config.content,
          subject_label: label,
          tables: config.content?.tables ?? [],
        },
      });
    }
  };

  const setSystemInstructions = (instructions: string) => {
    if (config) {
      setDraftConfig({
        ...config,
        content: {
          ...config.content,
          llm_filter_agent_instructions: instructions,
          tables: config.content?.tables ?? [],
        },
      });
    }
  };

  const clearDraftConfig = () => {
    setDraftConfig(undefined);
  };

  return (
    <ConfiguratorContext.Provider
      value={{
        config,
        editing,
        loading,
        addDomain,
        updateDomain,
        shiftDomain,
        removeDomain,
        setSystemInstructions,
        setSubjectLabel,
        clearDraftConfig,
        setDraftConfig,
      }}
    >
      {children}
    </ConfiguratorContext.Provider>
  );
};

const useConfiguratorContext = (): ConfiguratorContextType => {
  const context = useContext(ConfiguratorContext);
  if (!context) {
    throw new Error("useConfiguratorContext must be used within a ConfiguratorProvider");
  }
  return context;
};

export default useConfiguratorContext;
